import type React from 'react';
import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Check, FileDown, Pencil, X } from 'lucide-react';

import { Button } from '@/components/ui/button';

type HeaderBarProps = {
  videoName?: string;
  lastUpdatedLabel?: string;
  onBackClick?: () => void;
  onVideosClick?: () => void;
  onOpenVideoEditor?: () => void;
  onDownload?: () => void;
  onVideoNameChange?: (name: string) => void;
};

const HeaderBar: React.FC<HeaderBarProps> = ({
  videoName = 'Video.mp4',
  // lastUpdatedLabel = '12.04.2024, 3:25pm',
  // onBackClick,
  // onVideosClick,
  // onOpenVideoEditor,
  onDownload,
  onVideoNameChange,
}) => {
  const { t } = useTranslation();

  const lastDotIndex = videoName.lastIndexOf('.');
  const initialBaseName = lastDotIndex > 0 ? videoName.slice(0, lastDotIndex) : videoName;
  const extension = lastDotIndex > 0 ? videoName.slice(lastDotIndex) : '';

  const [baseName, setBaseName] = useState(initialBaseName);
  const [draftBaseName, setDraftBaseName] = useState(initialBaseName);
  const [isEditingName, setIsEditingName] = useState(false);

  useEffect(() => {
    setBaseName(initialBaseName);
    setDraftBaseName(initialBaseName);
    setIsEditingName(false);
  }, [initialBaseName]);

  const fullName = `${baseName}${extension}`;

  const handleStartEdit = () => {
    setDraftBaseName(baseName);
    setIsEditingName(true);
  };

  const handleCancelEdit = () => {
    setDraftBaseName(baseName);
    setIsEditingName(false);
  };

  const handleConfirmEdit = () => {
    const trimmed = draftBaseName.trim();
    const nextBaseName = trimmed.length ? trimmed : initialBaseName;

    if (nextBaseName === baseName) {
      toast.error(t('video-name-unchanged'));
      setIsEditingName(false);
      return;
    }

    setBaseName(nextBaseName);
    setIsEditingName(false);
    toast.success(t('video-name-updated-success'));

    const nextFullName = `${nextBaseName}${extension}`;
    onVideoNameChange?.(nextFullName);
  };

  const editingControls = isEditingName ? (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="flex h-8 w-10 items-center justify-center rounded-full px-0"
        onClick={handleConfirmEdit}
      >
        <Check className="size-4 text-primary-light" />
      </Button>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="flex h-8 w-10 items-center justify-center rounded-full px-0"
        onClick={handleCancelEdit}
      >
        <X className="size-4 text-primary-light" />
      </Button>
    </>
  ) : (
    <button
      type="button"
      onClick={handleStartEdit}
      className="inline-flex items-center justify-center p-0 text-primary-light"
    >
      <Pencil className="size-6 stroke-current" />
    </button>
  );

  return (
    <div className="flex flex-col gap-2 px-6 py-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {/* <Button
            size="md"
            className="w-12 rounded-sm px-0 [&_svg]:h-8 [&_svg]:w-8"
            onClick={onBackClick}
          >
            <ChevronLeft className="text-primary-lighter" />
          </Button> */}

          <div className="text-[14px] font-bold leading-[26px] tracking-[0.02em] text-[#07B1BE]">
            {/*       <button
              type="button"
              onClick={onVideosClick}
              className="cursor-pointer text-left underline-offset-2 hover:underline"
            >
              {t('videos')}
            </button>
            <span className="mx-1">{'>'}</span>
            <span>{fullName}</span> */}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/*  <Button variant="outline" size="sm" className="gap-2 px-4" onClick={onOpenVideoEditor}>
            <Clapperboard className="size-5" />
            <span>{t('videoEditor')}</span>
          </Button> */}

          <Button size="sm" className="gap-2 px-5" onClick={onDownload} disabled={!onDownload}>
            <FileDown className="size-5" />
            <span>{t('download')}</span>
          </Button>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex h-8 items-center rounded-full px-3">
            {isEditingName ? (
              <input
                value={draftBaseName}
                onChange={(event) => setDraftBaseName(event.target.value)}
                className="w-full border-b border-gray-300 bg-transparent text-lg font-semibold text-primary-darker outline-none"
              />
            ) : (
              <span className="text-lg font-semibold">{fullName}</span>
            )}
          </div>

          <div className="flex w-[88px] items-center justify-center gap-2">{editingControls}</div>
        </div>

        {/*  <div className="text-xs font-semibold text-primary-darker">
          {t('lastUpdated')}: {lastUpdatedLabel}
        </div> */}
      </div>
    </div>
  );
};

export default HeaderBar;
